import { Spacer } from '@spritz-finance/ui'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FadePresence } from '../../../../components/FadePresence'
import { CenteredPageInner } from '../../../../components/Layout/PageContainer'
import { Row } from '../../../../components/Layout/Row'
import { PageLoading } from '../../../../components/PageLoading'

import { useScreenSize } from '../../../../context/useScreenSize'
import { BillsDataQuery } from '../../../../graphql/generated/graphql'
import { BankAccount } from '../../../offramp/components/BankAccounts'
import { FROM_QUESTIONS_PARAM } from '../../BillsPage'
import { LinkBills } from '../linkBill/LinkBills'
import { BillsList } from './BillsList'
import { SearchingForBills } from './SearchingForBills'
import { useBills } from './useBills'

export type Bill = BillsDataQuery['bills'][0]
export type Subscription = BillsDataQuery['subscriptions'][0]

const useFromSecurityQuestions = () => {
    const { state } = useLocation()
    return !!(state as any)?.[FROM_QUESTIONS_PARAM]
}

type BillsContent = 'loading' | 'billSearch' | 'cantReceive' | 'bills' | 'initialConnect'

const getContentKey = ({
    loading,
    billSearch,
    hasConnectedBills,
}: {
    loading: boolean
    billSearch: boolean
    hasConnectedBills: boolean
}): BillsContent => {
    if (loading) return 'loading'
    if (billSearch) return 'billSearch'
    if (!hasConnectedBills) return 'initialConnect'
    return 'bills'
}

const BillsContent = ({
    content,
    connectedBills,
    subscriptions,
    bankAccounts,
    onBillSearchComplete,
    foundBills,
    setFoundBills,
}: {
    content: BillsContent
    canAddBills: boolean
    connectedBills: Bill[]
    subscriptions: Subscription[]
    bankAccounts: BankAccount[]
    onBillSearchComplete: (foundBills: boolean) => void
    foundBills: boolean | null
    setFoundBills: React.Dispatch<React.SetStateAction<boolean | null>>
}) => {
    const { isMobile } = useScreenSize()

    switch (content) {
        case 'loading':
            return <PageLoading />
        case 'billSearch':
            return <SearchingForBills onBillSearchComplete={onBillSearchComplete} />
        case 'initialConnect':
            return <LinkBills foundBills={foundBills} hasExistingAccounts={false} />
        case 'cantReceive':
        default:
            return (
                <CenteredPageInner>
                    {isMobile && <Spacer height={16} />}
                    <Row align="center">
                        <LinkBills
                            floating={isMobile}
                            additional={true}
                            foundBills={foundBills}
                            setFoundBills={setFoundBills}
                            hasExistingAccounts={true}
                        />
                    </Row>
                    {!isMobile && <Spacer height={32} />}

                    <BillsList
                        bills={connectedBills}
                        bankAccounts={bankAccounts}
                        subscriptions={subscriptions}
                    />
                </CenteredPageInner>
            )
    }
}

export const Bills = () => {
    const { loading, hasConnectedBills, canAddBills, connectedBills, bankAccounts, subscriptions } =
        useBills()
    const fromSecurityQuestions = useFromSecurityQuestions()
    const [billSearch, setBillSearch] = useState(fromSecurityQuestions)
    const [foundBills, setFoundBills] = useState<boolean | null>(null)

    const onBillSearchComplete = (foundBills = false) => {
        setFoundBills(foundBills)
        setBillSearch(false)
    }

    const content = getContentKey({
        loading,
        billSearch,
        hasConnectedBills,
    })

    return (
        <FadePresence renderKey={content}>
            <BillsContent
                content={content}
                canAddBills={canAddBills}
                connectedBills={connectedBills}
                bankAccounts={bankAccounts}
                onBillSearchComplete={onBillSearchComplete}
                foundBills={foundBills}
                setFoundBills={setFoundBills}
                subscriptions={subscriptions}
            />
        </FadePresence>
    )
}
